/** @jsxImportSource @emotion/react */
import { Dispatch, SetStateAction, useEffect } from "react";
import * as React from "react";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { MediumText } from "../styles/styles";
import { BLACK, WHITE } from "../styles/colors";
import { ERROR_BORDER, WARNING_BORDER } from "../forms/styles";
import { FADE_IN_ANIMATION } from "../styles/animations";
import { IAppState } from "../core/redux/IAppState";
import { useSelector } from "react-redux";
import { formatDate, makeDateFrom, toDateOnlyString } from "../language/time";
import DateInput from "../forms/elements/DateInput";
import errorImgRed from "../assets/icons/failureTriangleRed.svg";
import warningImgGold from "../assets/icons/warningTriangleSunsetGold.svg";
import historyImg from "../assets/icons/history.svg";
import { Row } from "../layout/flexbox";
import TooltipView from "../layout/tooltip/TooltipView";
import { doNothing } from "../language/module";
import { getResource } from "../language/resources/getResource";
import { ValidationMetadataHistoryTable } from "./ValidationMetadataHistoryTable";
import {
  LiftAccessRuleHistory,
  LIFT_ACCESS_RULE_EVENT_CATEGORY,
  LiftAccessRuleEventDetail,
  EditableEffectiveDateRangeAccessRule,
} from "./models";
import { effectiveDateRangeRuleUniqueKey } from "./module";
import { useEffectiveDateRangeValidation } from "./useEffectiveDateRangeValidation";

type Props = {
  entitlement: EditableEffectiveDateRangeAccessRule;
  locked: boolean;
  setConfigurations: Dispatch<
    SetStateAction<EditableEffectiveDateRangeAccessRule[]>
  >;
  showChangeHistory: boolean;
  effectiveDateRangeHistory?: LiftAccessRuleHistory;
  validation: ReturnType<typeof useEffectiveDateRangeValidation>;
};

const ROW_ITEM_HEIGHT = 40;

const styles = {
  rowItemHeight: css`
    height: ${ROW_ITEM_HEIGHT}px;
  `,
  error: css`
    ${MediumText};
    background-color: ${BLACK};
    color: ${WHITE};
    padding: 10px;
    white-space: pre-line;
    max-width: 250px;
    border-radius: 0.5rem;
  `,
  errorBorder: css`
    ${ERROR_BORDER};
    box-sizing: border-box;
  `,
  warningBorder: css`
    ${WARNING_BORDER};
    box-sizing: border-box;
  `,
  row: css`
    td {
      vertical-align: middle !important;
      ${MediumText};
      ${FADE_IN_ANIMATION};
    }

    td > * {
      ${FADE_IN_ANIMATION};
    }
  `,
  metadataHistoryTable: css`
    padding: 15px;
    max-height: 300px;
    max-width: 480px;
    overflow-y: auto;
    transform: translate(-25%, 0);
  `,
};

const HistoryImage = styled("img")`
  margin-right: 5px;
  margin-left: 10px;
  height: 20px;
  opacity: 0.75;
`;

const ErrorImage = styled("img")`
  margin-right: 5px;
  margin-left: 5px;
  height: 25px;
  padding: 3px;
`;

const ErrorPlaceholder = styled("div")`
  margin-right: 5px;
  margin-left: 5px;
  height: 30px;
  width: 30px;
  padding: 3px;
`;

export const ValidationConfigurationRow: React.FC<Props> = (props) => {
  const resources = useSelector((state: IAppState) => state.locale);
  const dateFormat = useSelector((state: IAppState) => state.defaultDateFormat);

  const { entitlement, validation } = props;

  const safeDate = (value?: string) => {
    if (!value) return undefined;
    const d = makeDateFrom(value);
    return isNaN(d.getTime()) ? undefined : d;
  };

  const effectiveDate = safeDate(entitlement.effectiveDate);
  const expirationDate = safeDate(entitlement.expirationDate);

  const [showEffectiveDateTooltip, setShowEffectiveDateTooltip] =
    React.useState(false);
  const [showExpirationDateTooltip, setShowExpirationDateTooltip] =
    React.useState(false);

  const [showEffectiveDateHistory, setShowEffectiveDateHistory] =
    React.useState(false);
  const [showExpirationDateHistory, setShowExpirationDateHistory] =
    React.useState(false);

  const [effectiveDateHistory, setEffectiveDateHistory] = React.useState<
    LiftAccessRuleEventDetail[]
  >([]);
  const [expirationDateHistory, setExpirationDateHistory] = React.useState<
    LiftAccessRuleEventDetail[]
  >([]);

  useEffect(() => {
    setEffectiveDateHistory(
      props.effectiveDateRangeHistory?.historyEventDetails?.filter(
        (h) =>
          h.eventCategory ===
          LIFT_ACCESS_RULE_EVENT_CATEGORY.EFFECTIVE_DATE_UPDATED,
      ) || [],
    );

    setExpirationDateHistory(
      props.effectiveDateRangeHistory?.historyEventDetails?.filter(
        (h) =>
          h.eventCategory ===
          LIFT_ACCESS_RULE_EVENT_CATEGORY.EXPIRATION_DATE_UPDATED,
      ) || [],
    );
  }, [props.effectiveDateRangeHistory]);

  const handleUpdate = (field: string, date: Date | null) => {
    const updatedRow = {
      ...entitlement,
      [field]: date ? toDateOnlyString(date) : undefined,
    };

    props.setConfigurations((prev) =>
      prev.map((item) =>
        effectiveDateRangeRuleUniqueKey(item) ===
        effectiveDateRangeRuleUniqueKey(entitlement)
          ? updatedRow
          : item,
      ),
    );
  };

  const renderDateCell = (
    field: "effectiveDate" | "expirationDate",
    value: Date | undefined,
    showTooltip: boolean,
    setShowTooltip: (v: boolean) => void,
  ) => {
    const error = validation.getError(entitlement, field);
    const warning = validation.getWarning(entitlement, field);
    const hasError = validation.hasError(entitlement, field);
    const hasWarning = validation.hasWarning(entitlement, field);

    return (
      <Row>
        <div>
          <DateInput
            date={value}
            disabled={props.locked}
            onChange={(e) => handleUpdate(field, e)}
            placeholderText={getResource(
              resources,
              field === "effectiveDate"
                ? "VALIDATION_CONFIG_ROW_START_DATE"
                : "VALIDATION_CONFIG_ROW_EXPIRE_DATE",
            )}
            tooltipText={error ?? warning}
            dateFormat={dateFormat}
            rootCss={css`
              width: 9rem;
              ${hasError
                ? styles.errorBorder
                : hasWarning
                  ? styles.warningBorder
                  : ""}
            `}
          />
        </div>

        {error || warning ? (
          <TooltipView
            side="bottom"
            showTooltip={showTooltip}
            tooltipContent={<span css={styles.error}>{error ?? warning}</span>}
            onClickInside={() => setShowTooltip(!showTooltip)}
            onClickOutside={doNothing}
          >
            <ErrorImage
              src={hasError ? errorImgRed : warningImgGold}
              alt="status"
              title={error ?? warning}
            />
          </TooltipView>
        ) : (
          <ErrorPlaceholder />
        )}
      </Row>
    );
  };

  return (
    <tr>
      <td>{entitlement.product}</td>
      <td>{entitlement.season}</td>
      <td>{entitlement.fulfillmentSeason}</td>

      {!props.locked ? (
        <td>
          {renderDateCell(
            "effectiveDate",
            effectiveDate,
            showEffectiveDateTooltip,
            setShowEffectiveDateTooltip,
          )}
        </td>
      ) : (
        <td>
          <Row>
            {effectiveDate && formatDate(effectiveDate, dateFormat)}
            {props.showChangeHistory && effectiveDateHistory.length > 0 && (
              <TooltipView
                side="bottom"
                showTooltip={showEffectiveDateHistory}
                activateOnHover
                tooltipContent={
                  <ValidationMetadataHistoryTable
                    ruleHistory={effectiveDateHistory}
                    tableCss={styles.metadataHistoryTable}
                  />
                }
                onHoverInside={() => setShowEffectiveDateHistory(true)}
                onHoverOutside={() => setShowEffectiveDateHistory(false)}
                onClickInside={doNothing}
                onClickOutside={doNothing}
              >
                <HistoryImage src={historyImg} />
              </TooltipView>
            )}
          </Row>
        </td>
      )}

      {!props.locked ? (
        <td>
          {renderDateCell(
            "expirationDate",
            expirationDate,
            showExpirationDateTooltip,
            setShowExpirationDateTooltip,
          )}
        </td>
      ) : (
        <td>
          <Row>
            {expirationDate && formatDate(expirationDate, dateFormat)}
            {props.showChangeHistory && expirationDateHistory.length > 0 && (
              <TooltipView
                side="bottom"
                showTooltip={showExpirationDateHistory}
                activateOnHover
                tooltipContent={
                  <ValidationMetadataHistoryTable
                    ruleHistory={expirationDateHistory}
                    tableCss={styles.metadataHistoryTable}
                  />
                }
                onHoverInside={() => setShowExpirationDateHistory(true)}
                onHoverOutside={() => setShowExpirationDateHistory(false)}
                onClickInside={doNothing}
                onClickOutside={doNothing}
              >
                <HistoryImage src={historyImg} />
              </TooltipView>
            )}
          </Row>
        </td>
      )}
    </tr>
  );
};
